import type { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { bindingById } from '@pushly/models/lib/enums'

/** Key for the Nth Guidance statement. `style` is now an array of independently-extracted
 * statements (it was one synthesized paragraph, which is what made the tier unstable), so each
 * gets its own key exactly like `assertions`/`unmapped`.
 */
export const guidanceSpanKey = (i: number) => `guidance-${i}`

/**
 * One entry per row the Guaranteed section should SHOW, which is not one per PQuery condition.
 *
 * `banned_terms` is one `string_array` binding that the compiler fans out to one condition per
 * term, because `CONTENT_DIRECTIVE_PROFILE` is depth-1 and AND-only. Rendered literally that is
 * a wall of near-identical rows for what the author wrote as a sentence or two, pushing every
 * other tier below the fold. Collapsing them is more faithful to the compiled model, not less:
 * `resolved` holds one binding, and this restores it.
 *
 * Grouped by binding id and only for `string_array` — an integer limit or a flag is already one
 * rule per entry, and merging two length limits would be a lie.
 *
 * Sources are the ROW's, not the term's: every fanned-out entry carries the same source list,
 * since the compiler attributes the binding to every sentence that contributed a term.
 */
export interface IGuaranteedDisplayRow {
    /** Span key shared by every entry folded into this row. */
    key: string
    attribute: string
    operator: string
    /** An array when several terms were folded together, the entry's own value otherwise. */
    value: unknown
    sources: { start: number; end: number; quote: string }[]
}

export function guaranteedDisplayRows(compiled: Pick<CompiledDirective, 'resolved'>): IGuaranteedDisplayRow[] {
    const rows: IGuaranteedDisplayRow[] = []
    const groupedByBindingId = new Map<string, IGuaranteedDisplayRow>()

    ;(compiled.resolved ?? []).forEach((item, i) => {
        const binding = item.binding ? bindingById(item.binding.id) : undefined
        if (!binding) return

        const sources = (item.sources ?? []).map((s) => ({ start: s.start, end: s.end, quote: s.quote }))

        if (binding.value.kind !== 'string_array') {
            rows.push({
                key: `guaranteed-${i}`,
                attribute: binding.attribute,
                operator: binding.operator,
                value: item.binding.value,
                sources,
            })
            return
        }

        const existing = groupedByBindingId.get(binding.id)
        if (existing) {
            ;(existing.value as unknown[]).push(item.binding.value)
            // Sources repeat across the fan-out; keep one of each so a row does not paint the
            // same sentence several times over.
            for (const source of sources) {
                if (!existing.sources.some((s) => s.start === source.start && s.end === source.end)) {
                    existing.sources.push(source)
                }
            }
            return
        }

        // Keyed on the FIRST entry's index, so keys keep the same shape as every other tier's
        // and stay stable for a given compile.
        const row: IGuaranteedDisplayRow = {
            key: `guaranteed-${i}`,
            attribute: binding.attribute,
            operator: binding.operator,
            value: [item.binding.value],
            sources,
        }
        groupedByBindingId.set(binding.id, row)
        rows.push(row)
    })

    // Term sources are filtered once the row's full value list is known — see
    // `sourcesStatingATerm`.
    for (const row of groupedByBindingId.values()) {
        row.sources = sourcesStatingATerm(row.sources, row.value as unknown[])
    }

    return rows
}

/**
 * Keeps only the sentences that actually state one of the terms.
 *
 * The compiler attributes a `string_array` BINDING to every sentence it believes contributed,
 * and it over-reaches: `"www" should not be place before a web address` is do-not-use shaped, so
 * it gets listed as a banned-terms source despite naming no banned term. Measured in a real
 * directive, and the consequence was not cosmetic. Dropping the source is the right answer to
 * "this sentence contains none of these terms": we do not know that it contributed, so we do not
 * claim it.
 *
 * Case-insensitive, because the author's casing and the extracted term need not agree
 * ("Cutting-Edge" in prose, `cutting-edge` as the term).
 *
 * Returning EMPTY is a legitimate outcome — the row simply has no attribution. Falling back to
 * every quote would restore the over-claim this exists to remove.
 */
function sourcesStatingATerm(
    sources: { start: number; end: number; quote: string }[],
    terms: unknown[],
): { start: number; end: number; quote: string }[] {
    const literals = terms.filter((t): t is string => typeof t === 'string' && t.length > 0).map((t) => t.toLowerCase())
    if (literals.length === 0) return sources

    return sources.filter((source) => {
        const quote = source.quote.toLowerCase()
        return literals.some((term) => quote.includes(term))
    })
}

/**
 * The condition group to hand PQueryBuilder — the collapsed rows, not the raw `spec`.
 *
 * Display only. The persisted `spec` is what actually gets enforced and is never touched: this
 * is the same set of rules with the fan-out folded back up for reading.
 *
 * An empty set is `{}` rather than `{ and: [] }`, matching what the compiler stores — the PQuery
 * reader rejects empty groups.
 */
export function collapsedGuaranteedSpec(compiled: Pick<CompiledDirective, 'resolved'>): Record<string, unknown> {
    const conditions = guaranteedDisplayRows(compiled).map((row) => ({
        [row.attribute]: { [row.operator]: row.value },
    }))

    return conditions.length > 0 ? { and: conditions } : {}
}
